import { useCallback, useEffect, useState } from "react";
import {
  BriefcaseBusiness,
  CalendarDays,
  CheckCircle2,
  Clock3,
  History,
  NotebookText,
  RefreshCw,
  X,
} from "lucide-react";
import type { PocTenantView, PocTimelineView } from "@ubisafe/shared";
import { api, ApiClientError } from "../lib/api";

interface PocTimelineModalProps {
  tenant: PocTenantView;
  onClose: () => void;
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeZone: "UTC" }).format(
    new Date(`${value}T12:00:00.000Z`),
  );
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" }).format(new Date(value));
}

export function PocTimelineModal({ tenant, onClose }: PocTimelineModalProps) {
  const [timeline, setTimeline] = useState<PocTimelineView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setTimeline(await api.getPocTimeline(tenant.identifier));
    } catch (requestError) {
      setError(requestError instanceof ApiClientError ? requestError.message : "Não foi possível carregar a linha do tempo.");
    } finally {
      setLoading(false);
    }
  }, [tenant.identifier]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="modal-card timeline-modal" role="dialog" aria-modal="true" aria-labelledby="timeline-modal-title">
        <header className="modal-card__header">
          <div className="modal-card__title">
            <span className="modal-card__icon timeline-modal__icon"><History aria-hidden="true" /></span>
            <div>
              <span className="eyebrow eyebrow--dark">{tenant.name}</span>
              <h2 id="timeline-modal-title">Linha do tempo</h2>
              <small>Identificador {tenant.identifier}</small>
            </div>
          </div>
          <button className="icon-button" type="button" aria-label="Fechar" onClick={onClose}><X /></button>
        </header>

        <div className="timeline-modal__content">
          {loading && (
            <div className="timeline-state" role="status">
              <RefreshCw className="spin" aria-hidden="true" />
              <strong>Carregando ciclos concluídos…</strong>
            </div>
          )}

          {!loading && error && (
            <div className="timeline-state timeline-state--error" role="alert">
              <RefreshCw aria-hidden="true" />
              <strong>Não foi possível carregar</strong>
              <span>{error}</span>
              <button className="secondary-button secondary-button--small" onClick={() => void load()}>Tentar novamente</button>
            </div>
          )}

          {!loading && !error && timeline?.cycles.length === 0 && (
            <div className="timeline-state">
              <History aria-hidden="true" />
              <strong>Nenhum ciclo concluído</strong>
              <span>A linha do tempo será preenchida quando uma limpeza concluir uma POC.</span>
            </div>
          )}

          {!loading && !error && timeline && timeline.cycles.length > 0 && (
            <ol className="poc-timeline" aria-label={`Ciclos concluídos de ${tenant.name}`}>
              {timeline.cycles.map((cycle) => (
                <li key={cycle.id}>
                  <span className="poc-timeline__marker"><CheckCircle2 aria-hidden="true" /></span>
                  <article className="poc-timeline__card">
                    <header>
                      <div>
                        <span>POC concluída</span>
                        <h3>{cycle.lead}</h3>
                      </div>
                      <time dateTime={cycle.actualEndAt}>{formatDateTime(cycle.actualEndAt)}</time>
                    </header>
                    <dl>
                      <div><CalendarDays aria-hidden="true" /><dt>Período</dt><dd>{formatDate(cycle.startDate)} a {formatDate(cycle.expectedEndDate)}</dd></div>
                      <div><Clock3 aria-hidden="true" /><dt>Término real</dt><dd>{formatDateTime(cycle.actualEndAt)}</dd></div>
                      <div><BriefcaseBusiness aria-hidden="true" /><dt>Comercial</dt><dd>{cycle.commercialOwner}</dd></div>
                      {cycle.notes && <div className="poc-timeline__notes"><NotebookText aria-hidden="true" /><dt>Anotações</dt><dd>{cycle.notes}</dd></div>}
                    </dl>
                  </article>
                </li>
              ))}
            </ol>
          )}
        </div>
      </section>
    </div>
  );
}
